import {
  Box,
  Flex,
  FormControl,
  GridItem,
  HStack,
  SimpleGrid,
  Square,
  useBreakpointValue,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMeasure, useWindowSize } from "react-use";

import desktopImage from "adminComponents/assets/background-images/illustrated-background-desktop.svg";
import mobileImage from "adminComponents/assets/background-images/illustrated-background-mobile.svg";
import { Button } from "adminComponents/atoms/Button";
import { Card } from "adminComponents/atoms/Card";
import { FormErrorMessage } from "adminComponents/atoms/FormErrorMessage";
import { Heading } from "adminComponents/atoms/Heading";
import { IconTooltip } from "adminComponents/atoms/IconTooltip";
import { Input } from "adminComponents/atoms/Input";
import { Logo } from "adminComponents/atoms/Logo";
import { Text } from "adminComponents/atoms/Text";
import { pxToRem } from "adminComponents/utils/pxToRem";
import AppSpinner from "screens/App/components/AppSpinner";

import joinArt from "./resource/joinArt.svg";

interface IJoinCodeProps {
  onJoinSession: (joinCode: string) => void;
  isLoading?: boolean;
  joinCode?: string;
  error?: string;
}

export const JoinCode: React.FC<IJoinCodeProps> = ({
  onJoinSession,
  joinCode: initialJoinCode,
  isLoading = false,
  error,
}) => {
  const [joinCode, setJoinCode] = useState<string>(initialJoinCode || "");
  const [containerRef, { height: containerHeight }] =
    useMeasure<HTMLDivElement>();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJoinCode(
      event.target.value
        .trim()
        .replaceAll(/[^a-zA-Z0-9]/g, "")
        .toUpperCase()
    );
  };

  const styles = useMultiStyleConfig("AdminTemplateWithCenteredHeroOneColumn", {
    variant: "default",
  });

  const { t } = useTranslation("session", {
    keyPrefix: "joinCodeScreen",
    useSuspense: false,
  });

  const columns = useBreakpointValue({ base: 1, sm: 1, md: 2 });
  const isLandscape = columns === 2;
  const padding = useBreakpointValue({ base: 24, sm: 24, md: 28 }) || 24;
  const buttonTextSize = useBreakpointValue({
    base: "md",
    sm: "sm",
    md: "md",
    lg: "lg",
  });

  const { height } = useWindowSize();
  const portraitArtHeight = height * 0.25;
  const portraitJoinHeight = containerHeight - portraitArtHeight;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    onJoinSession(joinCode);
  };

  if (isLoading) {
    return <AppSpinner />;
  }

  return (
    <Box
      w="100%"
      h="100%"
      backgroundImage={{ base: mobileImage, md: desktopImage }}
      bgSize="cover"
      __css={styles.backgroundContainer}
    >
      <Square size="100%">
        <Card
          h="90%"
          w="90%"
          padding={pxToRem(3)}
          borderColor="primary.tan"
          backgroundColor="primary.white"
          variant="adminCardSmallBorder"
          overflow="hidden"
        >
          <SimpleGrid ref={containerRef} h="100%" columns={columns}>
            <GridItem h={isLandscape ? "100%" : pxToRem(portraitArtHeight)}>
              <Box
                h={isLandscape ? "100%" : pxToRem(portraitArtHeight)}
                w="100%"
                backgroundImage={joinArt}
                bgSize="cover"
                backgroundRepeat="no-repeat"
                borderLeftRadius={pxToRem(17)}
              />
            </GridItem>
            <GridItem p={pxToRem(10)}>
              <form
                style={{ height: "100%", width: "100%" }}
                onSubmit={onSubmit}
              >
                <Flex
                  h={isLandscape ? "100%" : pxToRem(portraitJoinHeight)}
                  alignContent="center"
                  flexDirection="column"
                  justifyContent="space-between"
                  p={pxToRem(padding)}
                >
                  <Logo width="50%" />
                  <Box>
                    <Heading
                      as="h1"
                      paddingBottom={pxToRem(padding)}
                      variant="adminH2"
                    >
                      {t("instructionText")}
                    </Heading>
                    <FormControl isInvalid={!!error}>
                      <Input onChange={onChange} value={joinCode} />
                      <FormErrorMessage>
                        {error && (
                          <HStack spacing={pxToRem(10)}>
                            <Box as="span">{error}</Box>
                            <IconTooltip>{t("errorTooltipText")}</IconTooltip>
                          </HStack>
                        )}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Button
                    variant="adminButtonFilled"
                    size={buttonTextSize}
                    type="submit"
                  >
                    <Text as="span" size="lg" fontStyle="gameText">
                      {t("joinButton")}
                    </Text>
                  </Button>
                </Flex>
              </form>
            </GridItem>
          </SimpleGrid>
        </Card>
      </Square>
    </Box>
  );
};
